import styled from '@emotion/styled';
import { ChangeEvent, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { useAnonymousAnalyticsTrackPage } from '../../common/analytics';
import { IconArrowRight } from '../../common/design-tokens/Icons';
import { validateEmail } from '../../common/utils';
import FloatingLabelInput from '../shared/FloatingLabelInput';
import { translateError } from './authenticationUtils';
import InvitationAuthenticationPageBase from './InvitationAuthenticationPageBase';
import PasswordField from './PasswordField';
import { SubmitButton } from './styledComponents';

const InputContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacers.xs};
`;

const SubmitContainer = styled.div`
  margin-top: ${({ theme }) => theme.spacers.l};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
`;

const UnderLinedLink = styled(Link)`
  ${({ theme }) => theme.textStyles.labelL};
  color: ${({ theme }) => theme.colors.blueDark100};
  text-decoration-line: underline;
  width: 100%;
  text-align: center;
  margin-top: ${({ theme }) => theme.spacers.l};
  ${({ theme }) => theme.mediaQuery.md} {
    margin-top: 0;
    width: 50%;
    text-align: right;
  }
`;

function ForgotPasswordLink() {
  return (
    <UnderLinedLink to="/user/forgot-password">Forgot password?</UnderLinedLink>
  );
}

function InvitationSignInForm({
  error,
  token,
  xsrf,
}: {
  error: string | null;
  token: string;
  xsrf: string;
}) {
  const [email, setEmail] = useState('');
  const [isEmailAddressValid, setIsEmailAddressValid] = useState<
    boolean | undefined
  >(undefined);
  const [password, setPassword] = useState<string>('');
  const intl = useIntl();

  useAnonymousAnalyticsTrackPage({
    name: 'Invitation Sign In',
    category: 'Public',
    properties: {
      error,
    },
  });

  const hasEmailError = error === 'invalid_email';

  const emailErrorMessage =
    error === 'invalid_email'
      ? translateError('invalid_email')
      : translateError('email_in_use');

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    const targetEmail = event.target.value;
    setEmail(targetEmail);
    if (!isEmailAddressValid) {
      setIsEmailAddressValid(validateEmail(targetEmail));
    }
  }

  const shouldDisableLogin = !isEmailAddressValid || !password;

  return (
    <form method="POST" action="/api/account/login">
      <input type="hidden" name="token" value={token} />
      <input type="hidden" name="_xsrf" value={xsrf} />
      <InputContainer>
        <FloatingLabelInput
          key="email"
          name="email"
          value={email}
          error={
            isEmailAddressValid === false
              ? 'Not valid email'
              : hasEmailError
                ? emailErrorMessage
                : undefined
          }
          inputProps={{
            'aria-invalid': isEmailAddressValid === false,
            autoComplete: 'email',
          }}
          label="Email"
          data-testid="signin-email"
          onChange={handleEmailChange}
          onBlur={() => setIsEmailAddressValid(validateEmail(email))}
        />
      </InputContainer>
      <InputContainer>
        <PasswordField
          name="password"
          value={password}
          label={intl.formatMessage({
            id: 'general_password_fieldtext',
          })}
          inputProps={{
            autoComplete: 'current-password',
          }}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
        />
      </InputContainer>
      <SubmitContainer>
        <SubmitButton
          size="xsmall"
          rounded
          icon="end"
          type="submit"
          disabled={shouldDisableLogin}
        >
          Sign In
          <IconArrowRight aria-hidden />
        </SubmitButton>
        <ForgotPasswordLink />
      </SubmitContainer>
    </form>
  );
}

export default function InvitationSignIn() {
  return (
    <InvitationAuthenticationPageBase type="signin">
      {InvitationSignInForm}
    </InvitationAuthenticationPageBase>
  );
}
